import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Heart, 
  Crown, 
  Sparkles,
  FileText,
  User,
  Home,
  Mail,
  CheckCircle,
  Eye,
  Shield
} from 'lucide-react';
import ElegantSidebar from '../../../shared/components/layout/ElegantSidebar';
import '../styles/QRPaperCommon.css';
import { 
  ConsentPageLocationState,
  ConsentData,
  RoomInfo,
  QRPaperOrderApiRequest
} from '../../../shared/types/qrPaper';

// 確認画面用のLocationState型（同意データを含む）
type ConfirmPageLocationState = ConsentPageLocationState & {
  consentData: ConsentData;
};

const QRPaperConfirmForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ConfirmPageLocationState;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [isLoadingRoom, setIsLoadingRoom] = useState(true);

  // リダイレクト処理とルーム情報取得
  useEffect(() => {
    if (!state?.roomid || !state?.qrUrl || !state?.selectedDesign || !state?.formData || !state?.consentData) {
      alert('注文情報が不正です。同意確認画面に戻ります。');
      navigate('/admin/qr-paper/consent');
      return;
    }

    // ルーム情報を取得
    const fetchRoomInfo = async () => {
      try {
        setIsLoadingRoom(true);
        const baseUrl = process.env.REACT_APP_URL || 'http://localhost:8081';
        const response = await fetch(`${baseUrl}/api/room/${state.roomid}`);
        
        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          setRoomInfo({
            roomid: data.roomid,
            coupleName: data.coupleName
          });
        } else {
          console.error('ルーム情報の取得に失敗:', data.message);
          setRoomInfo({
            roomid: state.roomid,
            coupleName: state.roomid // フォールバック
          });
        }
      } catch (error) {
        console.error('ルーム情報取得エラー:', error);
        setRoomInfo({
          roomid: state.roomid,
          coupleName: state.roomid // フォールバック
        });
      } finally {
        setIsLoadingRoom(false);
      }
    };

    fetchRoomInfo();
  }, [state, navigate]);

  // 画面サイズに応じてサイドバー状態を設定
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const newIsDesktop = width >= 1024;
      setIsDesktop(newIsDesktop);
      
      if (newIsDesktop && !isDesktop) {
        setSidebarOpen(false);
      } else if (!newIsDesktop && isDesktop) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDesktop]);

  // デザイン名を取得
  const getDesignName = (designId: string, designType?: string): string => {
    if (designType === 'original' && designId === 'custom') {
      return 'オリジナルデザイン';
    }
    
    const designs = {
      elegant: 'エレガント',
      romantic: 'ロマンティック',
      welcome: 'ウェルカム'
    };
    return designs[designId as keyof typeof designs] || designId;
  };

  // 注文確定処理（API送信）
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);

    try {
      // API送信データの構築
      const requestData: QRPaperOrderApiRequest = {
        roomId: state.roomid,
        qrUrl: state.qrUrl,
        selectedDesign: state.selectedDesign,
        designType: state.designType || 'template',
        originalDesignRequests: state.originalDesignRequests || '',
        userName: state.formData.userName,
        postalCode: state.formData.postalCode,
        prefecture: state.formData.prefecture,
        city: state.formData.city,
        addressLine: state.formData.addressLine,
        email: state.formData.email
      };

      const token = localStorage.getItem("token");

      console.log('📧 QRコード用紙作成依頼を送信:', requestData);
      
      // 既存APIエンドポイントを使用してバックエンドに送信
      const baseUrl = process.env.REACT_APP_URL || 'http://localhost:8081';
      const response = await fetch(`${baseUrl}/api/qr-paper/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'サーバーエラーが発生しました' }));
        throw new Error(errorData.message || `HTTP Error: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ QRコード用紙作成依頼が完了しました:', result);
      
      // 成功モーダル表示
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('注文送信エラー:', error);
      alert('送信に失敗しました。しばらく時間をおいてから再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/qr-paper/consent', { state: state });
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/admin/room-create');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarOverlayClick = () => {
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  };

  if (!state?.roomid) {
    return null; // リダイレクト処理中
  }

  return (
    <div className="wedding-admin-layout">
      {/* サイドバーオーバーレイ（モバイル時） */}
      {!isDesktop && sidebarOpen && (
        <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
      )}
      
      {/* サイドバー */}
      <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
        <ElegantSidebar />
      </div>
      
      <div className={`wedding-main-content ${isDesktop ? 'desktop' : 'mobile'}`}>
        {/* トグルボタン（モバイル時のみ） */}
        {!isDesktop && (
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            <FileText size={24} />
          </button>
        )}

        {/* ヘッダーセクション */}
        <div className="wedding-header">
          <div className="wedding-header-decoration">
            <div className="crown-icon">
              <Crown size={32} />
            </div>
            <div className="sparkle-icon sparkle-1">
              <Sparkles size={20} />
            </div>
            <div className="sparkle-icon sparkle-2">
              <Sparkles size={16} />
            </div>
          </div>
          <h1 className="wedding-title">
            QRコード用紙作成
            <Heart className="title-heart" size={40} />
          </h1>
          <p className="wedding-subtitle">Order Confirmation - ご注文内容の確認</p>
        </div>

        {/* メインコンテンツ */}
        <div className="qr-paper-content">
          {/* 進行状況インジケーター */}
          <div className="progress-indicator">
            <div className="progress-step completed">
              <div className="step-number">1</div>
              <div className="step-label">デザイン選択</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step completed">
              <div className="step-number">2</div>
              <div className="step-label">情報入力</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step completed">
              <div className="step-number">3</div>
              <div className="step-label">同意確認</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step active">
              <div className="step-number">4</div>
              <div className="step-label">最終確認</div>
            </div>
          </div>

          {/* 注文内容確認カード */}
          <div className="wedding-card confirm-summary-card">
            <div className="card-header">
              <h2 className="card-title">ご注文内容の最終確認</h2>
              <p className="qr-card-description">以下の内容で注文を確定いたします。内容をご確認ください。</p>
            </div>

            <div className="confirm-content">
              {/* ルーム・デザイン情報 */}
              <div className="confirm-section">
                <h3 className="confirm-section-title">
                  <FileText size={18} />
                  ルーム・デザイン情報
                </h3>
                <div className="confirm-items">
                  <div className="confirm-item">
                    <span className="confirm-label">ルーム:</span>
                    <span className="confirm-value">
                      {isLoadingRoom ? (
                        '読み込み中...'
                      ) : (
                        <>
                          {roomInfo?.coupleName || state.roomid}
                          {roomInfo?.coupleName && roomInfo.coupleName !== state.roomid && (
                            <span className="room-id-detail"> ({state.roomid})</span>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="confirm-item">
                    <span className="confirm-label">選択デザイン:</span>
                    <span className="confirm-value">{getDesignName(state.selectedDesign, state.designType)}</span>
                  </div>
                  {state.designType === 'original' && state.originalDesignRequests && (
                    <div className="confirm-item original-design-requests">
                      <span className="confirm-label">オリジナルデザイン要望:</span>
                      <div className="confirm-value">
                        <div className="original-requests-display">
                          {state.originalDesignRequests}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* お客様情報 */}
              <div className="confirm-section">
                <h3 className="confirm-section-title">
                  <User size={18} />
                  お客様情報
                </h3>
                <div className="confirm-items">
                  <div className="confirm-item">
                    <span className="confirm-label">お名前:</span>
                    <span className="confirm-value">{state.formData.userName}</span>
                  </div>
                  <div className="confirm-item">
                    <span className="confirm-label">郵便番号:</span>
                    <span className="confirm-value">〒{state.formData.postalCode}</span>
                  </div>
                  <div className="confirm-item">
                    <span className="confirm-label">ご住所:</span>
                    <span className="confirm-value">
                      {state.formData.prefecture} {state.formData.city} {state.formData.addressLine}
                    </span>
                  </div>
                  <div className="confirm-item">
                    <span className="confirm-label">メールアドレス:</span>
                    <span className="confirm-value">{state.formData.email}</span>
                  </div>
                </div>
              </div>

              {/* 同意確認状況 */}
              <div className="confirm-section">
                <h3 className="confirm-section-title">
                  <Shield size={18} />
                  個人情報の取り扱い同意状況
                </h3>
                <div className="consent-status">
                  <div className="consent-status-item">
                    <CheckCircle size={16} className="consent-check-icon" />
                    <span>個人情報の利用目的について同意済み</span>
                  </div>
                  <div className="consent-status-item">
                    <CheckCircle size={16} className="consent-check-icon" />
                    <span>個人情報の処理・保管について同意済み</span>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* アクションボタン */}
          <div className="action-buttons">
            <button 
              onClick={handleBack}
              className="wedding-button secondary-button back-button"
              disabled={isSubmitting}
            >
              <ArrowLeft size={20} />
              戻る
            </button>
            
            <button 
              onClick={handleSubmitOrder}
              className="wedding-button primary-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="loading-spinner"></div>
                  送信中...
                </>
              ) : (
                <>
                  <Send size={20} />
                  注文を確定
                </>
              )}
            </button>
          </div>
        </div>

        {/* フッター装飾 */}
        <div className="wedding-footer">
          <div className="footer-decoration">
            <div className="decoration-line"></div>
            <Heart className="footer-heart" />
            <div className="decoration-line"></div>
          </div>
          <p className="footer-text">Your Order, Our Promise</p>
        </div>
      </div>

      {/* 送信完了モーダル */}
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="modal-content success-modal">
            <div className="modal-header">
              <div className="modal-title-container">
                <CheckCircle size={24} className="success-icon" />
                <h3>注文完了</h3>
              </div>
            </div>
            
            <div className="modal-body">
              <div className="success-message">
                <CheckCircle size={48} className="success-icon-large" />
                <h4>QRコード用紙の注文を承りました</h4>
                <p>
                  ご入力いただいた情報で用紙を作成し、指定のご住所へお送りいたします。<br />
                  作成完了時には、メールでご連絡いたします。<br />
                  <br />
                  <small style={{ color: '#6b7280', fontSize: '0.9em' }}>
                    ※ お客様情報は安全に保存され、今後のサービス向上に活用させていただきます。
                  </small>
                </p>
              </div>
              
              <div className="order-details">
                <div className="detail-item">
                  <span>ルーム:</span>
                  <span>
                    {roomInfo?.coupleName || state.roomid}
                    {roomInfo?.coupleName && roomInfo.coupleName !== state.roomid && (
                      <span className="room-id-detail"> ({state.roomid})</span>
                    )}
                  </span>
                </div>
                <div className="detail-item">
                  <span>デザイン:</span>
                  <span>{getDesignName(state.selectedDesign, state.designType)}</span>
                </div>
                <div className="detail-item">
                  <span>お名前:</span>
                  <span>{state.formData.userName}</span>
                </div>
                <div className="detail-item">
                  <span>ご住所:</span>
                  <span>〒{state.formData.postalCode} {state.formData.prefecture} {state.formData.city} {state.formData.addressLine}</span>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={handleSuccessModalClose}
                className="wedding-button primary-button"
              >
                管理画面に戻る
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRPaperConfirmForm;